// Pantalla "Configuración del Sistema" — solo accesible para Super Usuarios.
// Es el panel central que reúne:
//   · Métricas globales del sistema
//   · Estado de integraciones (con secretos enmascarados)
//   · Catálogo de módulos + acciones (lectura)
//   · Accesos directos a cuestionarios, perfiles, roles
//   · Promoción/degradación de Super Admins
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import GoogleWorkspaceCard from "@/components/GoogleWorkspaceCard";
import {
  Settings, ShieldCheck, BookOpen, Layers, Users, Building2,
  Database, Mail, Cloud, Sparkles, CheckCircle2, AlertCircle,
  ArrowRight, FileQuestion, Activity, KeyRound, UserPlus, X, Loader2,
} from "lucide-react";

function MetricTile({ icon: Icon, label, value, accent = "#2C49A0", to }) {
  const Body = (
    <div className="jasq-card p-4 hover:shadow-lg transition-shadow group">
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${accent}1A`, color: accent }}>
          <Icon size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#6E7591]">{label}</p>
          <p className="text-2xl font-extrabold text-[#1F2547] tabular-nums">{value}</p>
        </div>
        {to && <ArrowRight size={16} className="text-[#6E7591] group-hover:text-[#1F2547] group-hover:translate-x-0.5 transition-all" />}
      </div>
    </div>
  );
  return to ? <Link to={to}>{Body}</Link> : Body;
}

function IntegrationCard({ icon: Icon, name, configured, details, accent }) {
  return (
    <div className="jasq-card p-4">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}1A`, color: accent }}>
          <Icon size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between">
            <p className="font-display font-extrabold text-[#1F2547]">{name}</p>
            <span className={`jasq-pill text-xs ${configured ? "bg-[#E7F4E9] text-[#2A7A36]" : "bg-[#FFE9F0] text-[#A52258]"}`}>
              {configured ? (
                <><CheckCircle2 size={11} /> Activo</>
              ) : (
                <><AlertCircle size={11} /> Sin configurar</>
              )}
            </span>
          </div>
          <dl className="mt-2 space-y-0.5 text-xs">
            {details.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-2">
                <dt className="text-[#6E7591]">{k}</dt>
                <dd className="font-mono text-[#1F2547] text-right truncate max-w-[200px]">{v || "—"}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}

export default function AdminSystemConfig() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ email: "", name: "", password: "" });
  const [creating, setCreating] = useState(false);

  const reloadAdmins = async () => {
    const adms = await api.get("/users?role=admin");
    setAdmins(adms.data || []);
  };

  useEffect(() => {
    Promise.all([
      api.get("/system/config"),
      api.get("/users?role=admin"),
    ])
      .then(([cfg, adms]) => {
        setConfig(cfg.data);
        setAdmins(adms.data || []);
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo cargar la configuración"))
      .finally(() => setLoading(false));
  }, []);

  const toggleSuper = async (u, next) => {
    try {
      await api.patch(`/system/users/${u.id}/super-admin`, { is_super_admin: next });
      setAdmins((arr) => arr.map((x) => x.id === u.id ? { ...x, is_super_admin: next } : x));
      toast.success(next ? "Promovido a Super Usuario" : "Degradado a admin estándar");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  const handleCreateSuper = async (e) => {
    e.preventDefault();
    if (!createForm.email || !createForm.name || !createForm.password) {
      return toast.error("Todos los campos son obligatorios");
    }
    if (createForm.password.length < 6) {
      return toast.error("La contraseña debe tener al menos 6 caracteres");
    }
    setCreating(true);
    try {
      const { data } = await api.post("/system/super-admins", createForm);
      toast.success(
        data.promoted_existing
          ? `Usuario ya existía — promovido a Super Usuario`
          : `Super Usuario creado: ${data.email}`
      );
      setCreateOpen(false);
      setCreateForm({ email: "", name: "", password: "" });
      await reloadAdmins();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al crear");
    } finally {
      setCreating(false);
    }
  };

  if (!user?.is_super_admin) {
    return (
      <div className="jasq-card p-8 text-center" data-testid="system-config-denied">
        <ShieldCheck size={42} className="mx-auto text-[#A52258] mb-3" />
        <h2 className="font-display text-xl font-extrabold text-[#1F2547]">Acceso restringido</h2>
        <p className="text-sm text-[#6E7591] mt-2">Solo Super Usuarios pueden acceder a esta sección.</p>
      </div>
    );
  }

  if (loading || !config) {
    return <div className="text-center text-[#6E7591] py-12">Cargando configuración…</div>;
  }

  const m = config.metrics;
  const ints = config.integrations;
  const totalActions = Object.values(config.module_actions).reduce((acc, list) => acc + list.length, 0);

  return (
    <div className="space-y-6" data-testid="system-config-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#A43790]">Configuración</p>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#1F2547] inline-flex items-center gap-3">
            <Settings size={30} className="text-[#A43790]" />
            Configuración del Sistema
          </h1>
          <p className="text-sm text-[#6E7591] mt-1">
            Panel central reservado a Super Usuarios. Edita cuestionarios, gestiona integraciones, promueve administradores.
          </p>
        </div>
        <span className="jasq-pill bg-[#FFF7DA] text-[#9C7A18]" data-testid="super-admin-badge">
          <Sparkles size={12} /> Super Usuario activo
        </span>
      </div>

      {/* Métricas */}
      <section className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <MetricTile icon={Users} label="Usuarios" value={m.users} accent="#2C49A0" to="/admin/usuarios" />
        <MetricTile icon={Building2} label="Franquicias" value={m.franchises} accent="#F5B82E" to="/admin/franquicias" />
        <MetricTile icon={Layers} label="Perfiles de Acceso" value={m.permission_profiles} accent="#A43790" to="/admin/perfiles-permisos" />
        <MetricTile icon={FileQuestion} label="Plantillas CEP/CAD" value={m.questionnaire_templates} accent="#3FA84D" to="/admin/cuestionarios" />
        <MetricTile icon={ShieldCheck} label="Super Usuarios" value={m.super_admins} accent="#D63384" />
      </section>

      {/* Atajos */}
      <section className="jasq-card p-5">
        <h2 className="font-display font-extrabold text-[#1F2547] inline-flex items-center gap-2 mb-3">
          <BookOpen size={18} className="text-[#A43790]" /> Accesos rápidos
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Link to="/admin/cuestionarios" data-testid="shortcut-cuestionarios" className="jasq-btn-ghost inline-flex items-center justify-center gap-2 text-sm">
            <FileQuestion size={14} /> Cuestionarios (CEP/CAD)
          </Link>
          <Link to="/admin/perfiles-permisos" className="jasq-btn-ghost inline-flex items-center justify-center gap-2 text-sm">
            <Layers size={14} /> Perfiles de Acceso
          </Link>
          <Link to="/admin/roles" className="jasq-btn-ghost inline-flex items-center justify-center gap-2 text-sm">
            <ShieldCheck size={14} /> Roles
          </Link>
          <Link to="/admin/auditoria" className="jasq-btn-ghost inline-flex items-center justify-center gap-2 text-sm">
            <Activity size={14} /> Auditoría
          </Link>
        </div>
      </section>

      {/* Integraciones */}
      <section>
        <h2 className="font-display font-extrabold text-[#1F2547] inline-flex items-center gap-2 mb-3">
          <KeyRound size={18} className="text-[#A43790]" /> Integraciones · estado
        </h2>
        <div className="mb-3">
          <GoogleWorkspaceCard />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="integrations-grid">
          <IntegrationCard
            icon={Database} name="MongoDB Atlas" accent="#0DB55C" configured={ints.mongodb.connected}
            details={[["Base de datos", ints.mongodb.db_name]]}
          />
          <IntegrationCard
            icon={Cloud} name="Cloudinary" accent="#3448C5" configured={ints.cloudinary.configured}
            details={[["Cloud name", ints.cloudinary.cloud_name], ["API key", ints.cloudinary.api_key_preview]]}
          />
          <IntegrationCard
            icon={Mail} name="SMTP / Gmail" accent="#D44638" configured={ints.smtp.configured}
            details={[["Usuario", ints.smtp.user], ["Password", ints.smtp.password_preview]]}
          />
          <IntegrationCard
            icon={Sparkles} name="Emergent LLM (Claude · Gemini · OpenAI)" accent="#A43790" configured={ints.emergent_llm.configured}
            details={[["Universal Key", ints.emergent_llm.key_preview]]}
          />
        </div>
        <p className="text-[11px] text-[#6E7591] mt-2">
          Las credenciales se enmascaran por seguridad — solo se muestran los últimos 4 caracteres. Para rotarlas, edita las variables de entorno del backend (<code>backend/.env</code>) y redespliega.
        </p>
      </section>

      {/* Administradores · Promoción Super Usuario */}
      <section className="jasq-card p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
          <h2 className="font-display font-extrabold text-[#1F2547] inline-flex items-center gap-2">
            <ShieldCheck size={18} className="text-[#A43790]" /> Super Usuarios del sistema
          </h2>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            data-testid="create-super-admin-btn"
            className="jasq-btn-primary inline-flex items-center gap-2 text-xs"
          >
            <UserPlus size={14} /> Crear Super Usuario
          </button>
        </div>
        <p className="text-xs text-[#6E7591] mb-3">
          Los Super Usuarios tienen acceso ilimitado a TODOS los módulos, pueden modificar cuestionarios CEP/CAD y gestionar esta pantalla. Promueve solo cuentas de confianza.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]" data-testid="super-admins-table">
            <thead>
              <tr className="border-b border-[#E1E5F0] bg-[#F7F8FA]">
                {["Administrador", "Email", "Estado actual", "Acción"].map((h) => (
                  <th key={h} className="text-left text-[10px] uppercase tracking-wider font-extrabold text-[#6E7591] px-4 py-2">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => (
                <tr key={a.id} className="border-b border-[#E1E5F0] last:border-0">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <img src={a.avatar} alt="" className="w-8 h-8 rounded-full border border-[#E1E5F0]" />
                      <span className="font-bold text-[#1F2547] text-sm">{a.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#4C5478]">{a.email}</td>
                  <td className="px-4 py-3">
                    {a.is_super_admin ? (
                      <span className="jasq-pill bg-[#FFF7DA] text-[#9C7A18]"><Sparkles size={11} /> Super Usuario</span>
                    ) : (
                      <span className="jasq-pill bg-[#E8EEFB] text-[#2C49A0]">Admin estándar</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleSuper(a, !a.is_super_admin)}
                      disabled={a.id === user.id && a.is_super_admin}
                      data-testid={`toggle-super-${a.id}`}
                      title={a.id === user.id && a.is_super_admin ? "No puedes degradarte a ti mismo" : ""}
                      className="text-xs font-bold px-3 py-1.5 rounded-lg border-2 border-[#E1E5F0] hover:border-[#A43790] hover:text-[#A43790] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      {a.is_super_admin ? "Degradar" : "Promover a Super"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Catálogo módulos (lectura) */}
      <section className="jasq-card p-5">
        <h2 className="font-display font-extrabold text-[#1F2547] mb-1">Catálogo de módulos · {config.modules.length} módulos · {totalActions} acciones</h2>
        <p className="text-xs text-[#6E7591] mb-3">Para editar las acciones por perfil, ve a <Link to="/admin/perfiles-permisos" className="text-[#A43790] font-bold underline">Perfiles de Acceso</Link>.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {config.modules.map((mod) => {
            const acts = config.module_actions[mod.id] || [];
            return (
              <div key={mod.id} className="border border-[#E1E5F0] rounded-xl px-3 py-2 bg-[#FAFBFE]">
                <div className="flex items-center justify-between">
                  <p className="font-bold text-[#1F2547] text-sm">{mod.id} · {mod.name}</p>
                  <span className={`text-[9px] uppercase font-extrabold px-1.5 py-0.5 rounded ${mod.status === "live" ? "bg-[#E7F4E9] text-[#2A7A36]" : "bg-[#FFF7DA] text-[#9C7A18]"}`}>
                    {mod.status === "live" ? "Activo" : "Próximamente"}
                  </span>
                </div>
                <p className="text-[11px] text-[#6E7591] mt-0.5">{mod.desc}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {acts.map((a) => (
                    <span key={a.id} className="text-[9px] font-mono bg-white border border-[#E1E5F0] rounded px-1 py-0.5 text-[#4C5478]">{a.id}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Modal · Crear Super Usuario */}
      {createOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="create-super-modal">
          <div className="absolute inset-0 bg-[#1F2547]/60 backdrop-blur-sm" onClick={() => setCreateOpen(false)} />
          <form
            onSubmit={handleCreateSuper}
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-[#E1E5F0]"
          >
            <div className="px-6 py-4 border-b border-[#E1E5F0] flex items-center justify-between">
              <h3 className="font-display text-lg font-extrabold text-[#1F2547] inline-flex items-center gap-2">
                <UserPlus size={18} className="text-[#A43790]" /> Crear Super Usuario
              </h3>
              <button type="button" onClick={() => setCreateOpen(false)} className="text-[#6E7591] hover:text-[#1F2547]">
                <X size={18} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-xs text-[#4C5478] bg-[#FFF7DA] border-2 border-[#F5B82E] rounded-lg p-3">
                ⚡ Crea un nuevo admin con acceso TOTAL al sistema en un solo paso. El usuario deberá cambiar su contraseña en el primer login.
              </p>
              <div>
                <label className="text-xs uppercase tracking-wider font-extrabold text-[#6E7591]">Nombre completo</label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 rounded-xl border-2 border-[#E1E5F0] focus:border-[#A43790] focus:outline-none text-sm"
                  data-testid="create-super-name"
                  required
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-extrabold text-[#6E7591]">Email</label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 rounded-xl border-2 border-[#E1E5F0] focus:border-[#A43790] focus:outline-none text-sm"
                  data-testid="create-super-email"
                  required
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-extrabold text-[#6E7591]">Contraseña inicial</label>
                <input
                  type="text"
                  value={createForm.password}
                  onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 rounded-xl border-2 border-[#E1E5F0] focus:border-[#A43790] focus:outline-none text-sm font-mono"
                  data-testid="create-super-password"
                  placeholder="Mín. 6 caracteres"
                  minLength={6}
                  required
                />
                <p className="text-[10px] text-[#6E7591] mt-1">Se forzará el cambio en el primer login.</p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#E1E5F0] flex items-center justify-end gap-2">
              <button type="button" onClick={() => setCreateOpen(false)} className="jasq-btn-ghost text-sm">
                Cancelar
              </button>
              <button
                type="submit"
                disabled={creating}
                data-testid="create-super-submit"
                className="jasq-btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-60"
              >
                {creating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {creating ? "Creando…" : "Crear Super Usuario"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
